import type { FieldValues } from 'react-hook-form';
import { useController } from 'react-hook-form';

import { FieldMessage } from '~/components/FieldMessage';
import type { ToggleProps } from '~/components/Toggle';
import { Toggle } from '~/components/Toggle';
import { cn } from '~/lib/cn';

import type { FormFieldProps } from '../../FormField';
import { FormField } from '../../FormField';

type FormFieldToggleProps<TFormFields extends FieldValues> = Omit<
  FormFieldProps<TFormFields>,
  'children'
> &
  Omit<ToggleProps, 'name' | 'className' | 'variant'> & {
    layout?: 'vertical' | 'horizontal';
  };

export function FormFieldToggle<TFormFields extends FieldValues>({
  name,
  label,
  className,
  disabled,
  readOnly,
  layout = 'vertical',
  ...toggleProps
}: FormFieldToggleProps<TFormFields>) {
  const {
    field,
    fieldState: { error },
  } = useController({ name, disabled });

  if (layout === 'horizontal') {
    return (
      <div className={cn('flex flex-col gap-sm', className)}>
        <div className="flex items-center justify-between">
          <label
            htmlFor={name}
            className={cn(
              'text-body-md',
              (disabled || readOnly) && 'text-subtle',
            )}
          >
            {label}
          </label>
          <Toggle
            {...field}
            id={name}
            pressed={field.value}
            onPressedChange={field.onChange}
            {...toggleProps}
            disabled={disabled || readOnly}
          />
        </div>
        {error ? (
          <FieldMessage state="invalid">{error.message}</FieldMessage>
        ) : null}
      </div>
    );
  }

  return (
    <FormField
      name={name}
      label={label}
      className={className}
      disabled={disabled}
      readOnly={readOnly}
    >
      <Toggle
        {...field}
        pressed={field.value}
        onPressedChange={field.onChange}
        aria-labelledby={name}
        variant="formField"
        {...toggleProps}
        disabled={disabled || readOnly}
      />
    </FormField>
  );
}

FormFieldToggle.displayName = 'FormFieldToggle';
